import { client } from '@/lib/api/client'
import { venueReservationCancelPath, venueReservationRefundEstimatePath } from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'

import type { ReservationCancelInput, ReservationCancelResultView, ReservationRefundEstimateView } from './reservationCancelTypes'

/** `Idempotency-Key` 헤더 이름 — 백엔드 `VenueReservationCancelV1Controller.IDEMPOTENCY_KEY_HEADER`와 동일. */
export const IDEMPOTENCY_KEY_HEADER = 'Idempotency-Key'

/**
 * `GET .../refund-estimate` (#672 요구 ①) — 지금 취소하면 얼마를 환불하게 되는지 조회.
 * 🚨 `baseTime` 기준 예상치다. 호출부가 이 값을 캐시해 취소 실행에 재사용하지 않는다 — 모달이 열릴
 * 때마다 새로 호출한다(`useReservationRefundEstimate`가 모달 오픈 동안만 활성화).
 */
export async function fetchReservationRefundEstimate(
  venueId: string,
  reservationId: number
): Promise<ReservationRefundEstimateView> {
  try {
    const { data } = await client.get<ReservationRefundEstimateView>(
      venueReservationRefundEstimatePath(venueId, reservationId)
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `POST .../cancel` (#672 요구 ②③) — 취소 처리 + 결제수단별 환불 실행.
 * 🚨 `Idempotency-Key` 헤더가 필수다. 같은 키의 재요청은 추가 환불 없이 기존 결과를 돌려준다
 * (`replayed: true`) — 호출부가 재시도 시 같은 키를 넘겨야 이중 환불을 막을 수 있다.
 */
export async function cancelReservation(
  venueId: string,
  reservationId: number,
  input: ReservationCancelInput,
  idempotencyKey: string
): Promise<ReservationCancelResultView> {
  try {
    const { data } = await client.post<ReservationCancelResultView>(
      venueReservationCancelPath(venueId, reservationId),
      input,
      { headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey } }
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}
